import { Box } from '@mui/material'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Typography } from './Typography'
import { Colors } from './color'

type TabItem<T extends string> = {
  value: T
  label: string
}

type Props<T extends string> = {
  items: readonly TabItem<T>[]
  value: T
  onChange: (value: T) => void
  size?: 'SP' | 'PC'
}

export const Tab = <T extends string>({ items, value, onChange, size = 'PC' }: Props<T>) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [indicator, setIndicator] = useState({ left: 0, width: 0 })

  const updateIndicator = () => {
    const index = items.findIndex((item) => item.value === value)
    const el = itemRefs.current[index]
    const container = containerRef.current
    if (!el || !container) return

    const elRect = el.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()

    setIndicator({
      left: elRect.left - containerRect.left,
      width: elRect.width,
    })
  }

  useLayoutEffect(() => {
    updateIndicator()
  }, [value, items])

  useEffect(() => {
    window.addEventListener('resize', updateIndicator)
    return () => window.removeEventListener('resize', updateIndicator)
  }, [value, items])

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        display: 'flex',
      }}
    >
      {items.map((item, index) => (
        <Box
          key={item.value}
          ref={(el: HTMLButtonElement | null) => {
            itemRefs.current[index] = el
          }}
          component='button'
          onClick={() => onChange(item.value)}
          sx={{
            background: 'transparent',
            border: 'none',
            color: Colors['Base/White'],
            whiteSpace: 'nowrap',
            padding: '0 12px 8px',
            cursor: 'pointer',
            transition: 'all 0.2s cubic-bezier(0.3, 0.3, 0.3, 1)',
            '&:hover': { backgroundColor: Colors['White/Alpha/08'] },
          }}
        >
          <Typography name={size === 'SP' ? 'Body/14/Medium' : 'Heading/18/Bold'}>
            {item.label}
          </Typography>
        </Box>
      ))}

      <div
        style={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          height: 3,
          width: indicator.width,
          backgroundColor: Colors['Samansa_Yellow/500'],
          transform: `translateX(${indicator.left}px)`,
          transition: 'all 0.2s cubic-bezier(0.3, 0.3, 0.3, 1)',
        }}
      />
    </div>
  )
}
